import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Container } from '../Layout/ResponsiveGrid';
import { Mail, Send, CheckCircle, AlertCircle, Loader, Github, Linkedin, MapPin, Youtube } from 'lucide-react';
import { BugContactDetector } from '../EasterEggs/BugShooterGame';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// Styled components
const ContactSection = styled.section`
  padding: 6rem 0;
  position: relative;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 4rem 0;
  }
`;

const BackgroundGradient = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 30%;
  background: linear-gradient(to top, ${({ theme }) => `${theme.colors.accent}05`}, transparent);
  z-index: -1;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem;
  
  @media (min-width: 992px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const ContactInfo = styled.div`
  animation: ${fadeIn} 0.8s ease-out forwards;
`;

const SectionHeading = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 1rem;
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 80px;
    height: 4px;
    background: ${({ theme }) => theme.colors.accent};
    border-radius: 2px;
  }
`;

const ContactText = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 1.1rem;
  line-height: 1.8;
  margin-bottom: 2.5rem;
  max-width: 500px;
`;

const ContactDetailsContainer = styled.div`
  margin-bottom: 3rem;
`;

// Modified ContactDetail component to include the BugContactDetector
const ContactDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  position: relative; /* Added to position the detector */
  
  &:hover {
    .icon-wrapper {
      background: ${({ theme }) => theme.colors.accent};
      color: white;
      transform: scale(1.1);
    }
  }
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${({ theme }) => `${theme.colors.accent}15`};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.accent};
  transition: all 0.3s ease;
`;

const DetailContent = styled.div``;

const DetailTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 0.25rem;
`;

const DetailText = styled.a`
  color: ${({ theme }) => theme.colors.textSecondary};
  text-decoration: none;
  font-size: 1rem;
  transition: color 0.3s ease;
  
  &:hover {
    color: ${({ theme }) => theme.colors.accent};
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.card};
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    background: ${({ theme }) => theme.colors.accent};
    color: white;
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;

const FormContainer = styled.div`
  background: ${({ theme }) => theme.colors.card};
  border-radius: 16px;
  padding: 2.5rem;
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  animation: ${fadeIn} 0.8s ease-out forwards;
  animation-delay: 0.2s;
  opacity: 0;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 1.5rem;
  }
`;

const FormHeader = styled.div`
  margin-bottom: 2rem;
  text-align: center;
`;

const FormTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 0.5rem;
`;

const FormSubtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 1rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: ${({ theme }) => theme.colors.text};
  font-size: 0.9rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  svg {
    color: ${({ theme }) => theme.colors.accent};
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  border-radius: 8px;
  background: ${({ theme }) => `${theme.colors.bg}50`};
  color: ${({ theme }) => theme.colors.text};
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.accent};
    box-shadow: 0 0 0 3px ${({ theme }) => `${theme.colors.accent}20`};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 0.9rem 1rem;
    font-size: 16px; // Prevents auto-zoom on iOS
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  border-radius: 8px;
  background: ${({ theme }) => `${theme.colors.bg}50`};
  color: ${({ theme }) => theme.colors.text};
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.accent};
    box-shadow: 0 0 0 3px ${({ theme }) => `${theme.colors.accent}20`};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 0.9rem 1rem;
    font-size: 16px; // Prevents auto-zoom on iOS
  }
`;

const CharCount = styled.div`
  font-size: 0.75rem;
  text-align: right;
  color: ${({ $isNearLimit, theme }) => 
    $isNearLimit ? '#ef4444' : theme.colors.textSecondary};
`;

const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 0.9rem 1.5rem;
  background: ${({ theme }) => theme.colors.accent};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;
  
  &:hover:not(:disabled) {
    transform: translateY(-3px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  
  .spinner {
    animation: ${rotate} 1.5s linear infinite;
  }
`;

const StatusMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: 8px;
  margin-top: 1rem;
  animation: ${fadeIn} 0.3s ease-out forwards;
  
  background: ${({ $type }) => 
    $type === 'success' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)'};
    
  color: ${({ $type }) => 
    $type === 'success' ? '#22c55e' : '#ef4444'};
    
  svg {
    flex-shrink: 0;
  }
`;

const FloatingDecorationContainer = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: -1;
`;

const FloatingDecoration = styled.div`
  position: absolute;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  background: ${({ theme }) => `${theme.colors.accent}10`};
  animation: ${float} ${({ $duration }) => $duration}s ease-in-out infinite;
  animation-delay: ${({ $delay }) => $delay}s;
  opacity: 0.8;
  
  &:nth-child(odd) {
    background: ${({ theme }) => `${theme.colors.secondary}30`};
  }
`;

const RedesignedContact = () => {
  const [formData, setFormData] = useState({
    email: '',
    subject: '',
    message: ''
  });
  
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });
    
    try {
      // Simulate form submission with a delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Success simulation
      setStatus({
        type: 'success',
        message: 'Message sent successfully! I will get back to you soon.'
      });
      
      setFormData({
        email: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Something went wrong. Please try again later.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Generate positions for floating decorations
  const decorations = [
    { size: 100, top: '10%', left: '5%', duration: 5, delay: 0 },
    { size: 150, top: '60%', left: '10%', duration: 7, delay: 1 },
    { size: 80, top: '30%', right: '10%', duration: 6, delay: 0.5 },
    { size: 120, bottom: '10%', right: '15%', duration: 8, delay: 1.5 }
  ];
  
  return (
    <ContactSection id="contact">
      <BackgroundGradient />
      <FloatingDecorationContainer>
        {decorations.map((decoration, index) => (
          <FloatingDecoration
            key={index}
            $size={decoration.size}
            $duration={decoration.duration}
            $delay={decoration.delay}
            style={{
              top: decoration.top || 'auto',
              left: decoration.left || 'auto',
              right: decoration.right || 'auto',
              bottom: decoration.bottom || 'auto'
            }}
          />
        ))}
      </FloatingDecorationContainer>
      
      <Container>
        <ContactGrid>
          <ContactInfo>
            <SectionHeading>Get In Touch</SectionHeading>
            <ContactText>
              Feel free to reach out if you have any questions, project ideas, or just want to connect. I'm always open to discussing new opportunities and collaborations.
            </ContactText>
            
            <ContactDetailsContainer>
              {/* Add BugContactDetector to Email and Location contact details */}
              <ContactDetail>
                <IconWrapper className="icon-wrapper">
                  <Mail size={20} />
                </IconWrapper>
                <DetailContent>
                  <DetailTitle>Email</DetailTitle>
                  <DetailText href="mailto:mail@pascalndietrich.de">
                    mail@pascalndietrich.de
                  </DetailText>
                </DetailContent>
                <BugContactDetector />
              </ContactDetail>
              
              <ContactDetail>
                <IconWrapper className="icon-wrapper">
                  <MapPin size={20} />
                </IconWrapper>
                <DetailContent>
                  <DetailTitle>Location</DetailTitle>
                  <DetailText as="span">
                    Dresden, Germany
                  </DetailText>
                </DetailContent>
                <BugContactDetector />
              </ContactDetail>
            </ContactDetailsContainer>
            
            <div>
              <h3 style={{ marginBottom: '1rem', fontSize: '1.1rem', fontWeight: 600 }}>
                Social Media
              </h3>
              <SocialLinks>
                <SocialLink 
                  href="https://github.com/pascalnd" 
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="GitHub Profile"
                >
                  <Github size={20} />
                </SocialLink>
                <SocialLink
                  href="https://linkedin.com/in/pascalnd"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn Profile"
                >
                  <Linkedin size={20} />
                </SocialLink>
                <SocialLink
                  href="https://www.youtube.com/@PascalNerD"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="YouTube Channel"
                >
                  <Youtube size={20} />
                </SocialLink>
              </SocialLinks>
            </div>
          </ContactInfo>
          
          <FormContainer>
            <FormHeader>
              <FormTitle>Send Me a Message</FormTitle>
              <FormSubtitle>I'll get back to you as soon as possible</FormSubtitle>
            </FormHeader>
            
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="email">
                  <Mail size={14} />
                  Email Address
                </Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="subject">Subject</Label>
                <Input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="What's this about?"
                  required
                  maxLength={200}
                />
                <CharCount $isNearLimit={formData.subject.length > 180}>
                  {formData.subject.length}/200 characters
                </CharCount>
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="message">Message</Label>
                <TextArea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Your message here..."
                  required
                  maxLength={3000}
                />
                <CharCount $isNearLimit={formData.message.length > 2700}>
                  {formData.message.length}/3000 characters
                </CharCount>
              </FormGroup>
              
              <SubmitButton 
                type="submit" 
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader size={18} className="spinner" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Send Message
                  </>
                )}
              </SubmitButton>
              
              {status.message && (
                <StatusMessage $type={status.type}>
                  {status.type === 'success' ? (
                    <CheckCircle size={20} />
                  ) : (
                    <AlertCircle size={20} />
                  )}
                  {status.message}
                </StatusMessage>
              )}
            </Form>
          </FormContainer>
        </ContactGrid>
      </Container>
    </ContactSection>
  );
};

export default RedesignedContact;